import { Link } from "react-router-dom";
import { EnvelopeSimple, ChatCircleText, DownloadSimple } from "@phosphor-icons/react";
import useSEO from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { FAQ } from "@/components/FAQ";
import { SUPPORT_EMAIL } from "@/lib/site";
import useT from "@/i18n";
import { breadcrumbLd, faqLd, webPageLd } from "@/lib/seo";

export default function Support() {
  const { t: T, lp } = useT();
  const s = T.support;
  const path = "/support";
  const title = s.seoTitle;
  const description = s.seoDesc;
  const crumbs = [{ name: s.crumb, path }];
  useSEO({ title, description, path, jsonLd: [webPageLd({ title, description, path: lp(path) }), breadcrumbLd(crumbs, T.crumbs.home, lp), faqLd(s.faq)] });

  const meta = [
    { icon: EnvelopeSimple, href: `mailto:${SUPPORT_EMAIL}`, ext: true },
    { icon: ChatCircleText, href: lp("/contact") },
    { icon: DownloadSimple, href: lp("/download") },
  ];
  const channels = s.channels.map(([t, d, cta], i) => ({ ...meta[i], t, d, cta }));

  return (
    <div data-testid="support-page">
      <PageHeader crumbs={crumbs} badge={s.badge} title={s.title} intro={s.intro} />
      <section className="max-w-7xl mx-auto safe-x sm:px-8 pb-12" aria-labelledby="channels-heading">
        <h2 id="channels-heading" className="sr-only">{s.channelsSr}</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {channels.map((c, i) => (
            <article key={c.t} className="glass rounded-2xl p-7 flex flex-col" data-testid={`support-channel-${i}`}>
              <c.icon size={28} weight="duotone" className="text-[#00BFFF]" aria-hidden="true" />
              <h3 className="font-display text-lg font-semibold mt-4">{c.t}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed flex-1 break-words">{c.d}</p>
              {c.ext ? <a href={c.href} className="btn-ghost mt-5 self-start">{c.cta}</a> : <Link to={c.href} className="btn-ghost mt-5 self-start">{c.cta}</Link>}
            </article>
          ))}
        </div>
      </section>
      <section className="max-w-4xl mx-auto safe-x sm:px-8 py-12" aria-labelledby="support-faq-heading">
        <h2 id="support-faq-heading" className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{s.faqTitle}</h2>
        <div className="mt-8"><FAQ key={T.htmlLang} items={s.faq} prefix="support-faq" /></div>
      </section>
    </div>
  );
}
